import { useState, useEffect } from "react";
import { Space, Button, Row, Col, Input, Form, notification, Typography, Select } from "antd";
import { LeftCircleOutlined } from "@ant-design/icons";
import { useGetLocationsQuery } from "../../../redux/services/locationsApi";
import { useGetUsersQuery } from "../../../redux/services/userApi";
import { useGetPartnersQuery } from "../../../redux/services/partnerApi";
import { useNavigate, useParams } from "react-router-dom";
import {
  useCreateSurveyMutation,
  useLazyGetSurveyByIdQuery,
  useUpdateSurveyMutation
} from "../../../redux/services/surveyAPI";
import { useLazyGetBrandModelsQuery } from "../../../redux/services/brandModelApi";
import ErrorDetails from "../../../components/ErrorDetails";
import { useLazyGetBookingByIdQuery } from "../../../redux/services/bookingAPI";
import {
  AltContactNumberInputElement,
  BrandModelSelect,
  BrandSelect,
  CheckBoxInput,
  CityManagerSelect,
  CitySelect,
  ContactNumberInputElement,
  CustomerCitySelect,
  DropDownSelectProps,
  InputElement,
  PincodeInputElement,
  StateSelect,
  ZoneSelect,
  NumberInputElement
} from "../common/FormsElements";
import { localizeDateTime, isUserHasRoleTypes } from "../../../utils";
import { useAppSelector } from "../../../redux/hooks";
import {
  broadSurveeyFinding1Options,
  broadSurveeyFinding2Options,
  yesNoOptions,
  categoryOptions,
  customerBillingOptions,
  completionStatusOptions
} from "../../../constants/helperConstants";

const SurveyForm = (props: any) => {
  const navigate = useNavigate();
  const params = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [api, contextHolder] = notification.useNotification();

  const onGoBack = () => {
    navigate(-1);
  };

  const bookingId = params?.bookingId;
  const surveyId = params?.surveyId;

  const isEditMode = surveyId != null;

  const [getBookingById, { data: bookingData }] = useLazyGetBookingByIdQuery();
  const [getSurveyById, { data: surveyData }] = useLazyGetSurveyByIdQuery();

  const selectedBookingData = bookingData?.booking;
  const selectedSurveyData = surveyData?.survey;

  const partnerIdFromSurvey = selectedSurveyData?.survey_partner?.id;
  const [partnerId, setPartnerId] = useState<string>();

  const [locationId, setLocationId] = useState(null);

  const [outStationForInstall, setOutStationForInstall] = useState(false);
  const [outstationClient, setOutstationClient] = useState(false);

  const { data: partnerData, refetch: refetchPartners } = useGetPartnersQuery({
    all_pages: true,
    location_id: locationId
  });

  const isApproved = selectedSurveyData?.approved_at;
  const isCompleted = selectedSurveyData?.status == "completed";
  const outstationDistRequired = isEditMode ?
    selectedSurveyData?.brand?.outstation_distance_required
    : selectedBookingData?.brand?.outstation_distance_required;

  useEffect(() => {
    if (locationId) {
      refetchPartners();
    }
  }, [locationId]);

  useEffect(() => {
    setLocationId(isEditMode ? selectedSurveyData?.city?.id : selectedBookingData?.city?.id);
  }, [selectedBookingData, selectedSurveyData]);

  useEffect(() => {
    if (surveyId) {
      getSurveyById(surveyId);
    }
  }, [surveyId]);

  useEffect(() => {
    if (bookingId) {
      getBookingById(bookingId);
    }
  }, [bookingId]);

  useEffect(() => {
    if (selectedSurveyData) {
      setOutStationForInstall(selectedSurveyData.is_outstation)
    }
  }, [selectedSurveyData])

  useEffect(() => {
    if (selectedSurveyData) {
      setOutstationClient(selectedSurveyData.is_outstation_for_client)
    }
  }, [selectedSurveyData])

  const { data: fieldAgentData } = useGetUsersQuery({
    role_type: "field_agent",
    partner_id: partnerId || partnerIdFromSurvey,
    all_pages: true
  });
  const fieldAgents = partnerId || partnerIdFromSurvey ? fieldAgentData?.users || [] : [];

  const { data: partnerManagerData } = useGetUsersQuery({
    role_type: "partner_manager",
    partner_id: partnerId || partnerIdFromSurvey
  });
  const partnerManager = partnerId || partnerIdFromSurvey ? partnerManagerData?.users || [] : [];

  const { data: zoneLocationsData } = useGetLocationsQuery({
    location_type: "zone",
    all_pages: true
  });
  const zones = zoneLocationsData?.locations;

  const auth = useAppSelector<any>((store) => store.auth);
  const user = auth.user;
  const isAdminUser = user?.roles?.some((role: { role_type: string }) =>
    ["super_user", "management"].includes(role.role_type)
  );

  const isSuperUser = user?.roles?.some((role: { role_type: string }) =>
    ["super_user"].includes(role.role_type)
  );

  const isMisUser = user?.roles?.some((role: { role_type: string }) =>
    ["mis"].includes(role.role_type)
  );

  const isEditUser = user?.roles?.some(
    (role: { role_type: string }) => ["mis", "super_user", "management"].includes(role.role_type) // management generally not considered editor user
  );
  const isFinanceController = (user: any) => {
    return isUserHasRoleTypes(user, ["finance_controller"]);
  };

  const isManager = user?.roles?.some((role: { role_type: string }) =>
    ["city_manager", "zonal_manager"].includes(role.role_type)
  );

  const extraCableStatus = Form.useWatch("extra_cable_payment_status", form);
  const sowStatus = Form.useWatch("sow_status", form);

  const isManagerEditingCompletedSurvey =
    isEditMode && !isSuperUser && isManager && selectedSurveyData?.status === "completed";

  const isMisUserEditingCompletedSurvey =
    isEditMode && isMisUser && selectedSurveyData?.status === "completed";
  const sendReportsToApi = selectedSurveyData?.brand?.send_reports_to_api;

  const handlePartnerChange = (value: string) => {
    form.setFieldsValue({
      survey_partner_id: value,
      assigned_field_agent1_id: null,
      assigned_field_agent2_id: null,
      assigned_partner_user_id: null
    });
    setPartnerId(value);
  };

  const handleSurveyFinding1Change = (value: string) => {
    form.setFieldsValue({
      summary_stage1: value
    });
  };

  const handleSurveyFinding2Change = (value: string) => {
    form.setFieldsValue({
      summary_stage2: value
    });
  };

  const onCityChange = (cityLocationId: any) => {
    setLocationId(cityLocationId);
    form.setFieldValue("survey_partner_id", undefined);
  };

  useEffect(() => {
    let fieldsValue: any;
    if (isEditMode === true) {
      let scheduled_survey_date_time = null;
      if (selectedSurveyData?.scheduled_survey_date_time) {
        const localDateTime = localizeDateTime(selectedSurveyData?.scheduled_survey_date_time);
        const [datePart, timePart] = localDateTime.split(", ");
        const [day, month, year] = datePart.split("/");
        const [hours, minutes, seconds] = timePart.split(":");

        scheduled_survey_date_time = `${year}-${month}-${day}T${hours}:${minutes}`;
      }

      fieldsValue = {
        ...selectedSurveyData,
        booking_id: selectedBookingData?.id,
        survey_partner_id: selectedSurveyData?.survey_partner?.id,
        assigned_partner_user_id: selectedSurveyData?.assigned_partner_user?.id,
        assigned_field_agent1_id: selectedSurveyData?.assigned_field_agent1?.id,
        assigned_field_agent2_id: selectedSurveyData?.assigned_field_agent2?.id,
        scheduled_survey_date_time: scheduled_survey_date_time
      };
    } else {
      fieldsValue = {
        ...selectedBookingData,
        booking_id: selectedBookingData?.id
      };
    }
    form.setFieldsValue({
      ...fieldsValue,
      brand_id: fieldsValue?.brand?.id,
      brand: fieldsValue?.brand?.name,
      booking_id: fieldsValue?.id,
      city_id: fieldsValue?.city?.id,
      state_id: fieldsValue?.state?.id,
      assigned_city_manager_id: fieldsValue?.assigned_city_manager?.id,
      brand_model_id: fieldsValue?.brand_model?.id
    });

    const data = zones?.find((zone: any) => zone.id === fieldsValue?.zone?.id);
    if (data) {
      form.setFieldsValue({
        zone_id: fieldsValue?.zone?.id
      });
    }
  }, [selectedBookingData, selectedSurveyData]);

  useEffect(() => {
    const data = zones?.find((zone: any) => zone.id === selectedBookingData?.zone?.id);
    if (data) {
      form.setFieldsValue({
        zone_id: selectedBookingData?.zone?.id
      });
    }
  }, [selectedBookingData, zones]);

  useEffect(() => {
    const brandId = selectedSurveyData?.brand?.id || selectedBookingData?.brand?.id;
    if (brandId) {
      triggerGetBrandModels({ all_pages: true, brand_id: brandId });
    }
  }, [selectedSurveyData, selectedBookingData]);

  const onFinish = (values: any) => {
    if (isEditMode !== true) {
      addSurvey(values, setLoading, form);
    } else {
      onUpdateSurvey(values, setLoading, form, surveyId);
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    api.info({
      message: "Operation failed",
      description: (
        <div>
          {errorInfo?.errorFields?.map((error: any) => (
            <p key={error?.name[0]}>
              {error?.name[0]} : {error?.errors?.join(",")}
            </p>
          ))}
        </div>
      ),
      placement: "top"
    });
    console.log("Failed:", errorInfo);
  };

  const [createSurvey] = useCreateSurveyMutation();
  const [triggerGetBrandModels, { data: brandModelsData }] = useLazyGetBrandModelsQuery();
  const brandModels = brandModelsData?.brand_models || [];

  const addSurvey = async (values: any, setLoading: any, form: any) => {
    try {
      setLoading(true);
      const scheduled_survey_date_time = new Date(values?.scheduled_survey_date_time).toISOString();
      const data = { ...values, booking_id: selectedBookingData?.id, scheduled_survey_date_time };
      await createSurvey({ survey: data }).unwrap();
      api.success({
        message: "Survey created successfully!",
        placement: "top"
      });
      form.resetFields();
      setLoading(false);
      navigate("/surveys");
    } catch (error: any) {
      api.info({
        message: "Survey creation failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const [updateSurvey] = useUpdateSurveyMutation();

  const onUpdateSurvey = async (values: any, setLoading: any, form: any, id: any) => {
    try {
      setLoading(true);
      if (values?.scheduled_survey_date_time) {
        values["scheduled_survey_date_time"] = new Date(
          values?.scheduled_survey_date_time
        ).toISOString();
      }
      const data = { ...values };
      await updateSurvey({ id, survey: data }).unwrap();
      api.success({
        message: "Survey updated successfully!",
        placement: "top"
      });
      setLoading(false);
      form.resetFields();
      if (window.history.length > 1) {
        history.back();
      } else {
        navigate("/surveys");
      }
    } catch (error: any) {
      api.info({
        message: "Update Survey failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  return (
    <>
      {contextHolder}
      <div style={{ paddingBottom: 50 }}>
        <Row style={{ marginBottom: 20 }}>
          <Col
            sm={24}
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            {isEditMode ? <h2>Update Survey</h2> : <h2>Create Survey</h2>}
            <Button onClick={onGoBack} size="middle" type="default" icon={<LeftCircleOutlined />}>
              Back
            </Button>
          </Col>
        </Row>

        <Form
          name="survey_form"
          form={form}
          initialValues={{ remember: true }}
          labelCol={{ span: 9 }}
          wrapperCol={{ span: 11 }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
          labelAlign="right"
        >
          {!isFinanceController(user) && !isMisUserEditingCompletedSurvey && (
            <>
              <Typography style={{ fontWeight: 500 }}>Customer Details</Typography>
              <Row gutter={16}>
                <Col span={12}>
                  <InputElement
                    label="Customer Name"
                    name="customer_name"
                    disabled={isManagerEditingCompletedSurvey && sendReportsToApi}
                    rules={[{ required: true, message: "Please Enter Customer Name" }]}
                  />
                  <InputElement label="Email" name="email" />
                  <InputElement
                    label="GST Number"
                    name="gst"
                    rules={[
                      { min: 15, message: "GST Number must be minimum 15 digit." },
                      { max: 15, message: "GST Number be maximum 15 digit." }
                    ]}
                  />
                </Col>
                <Col span={12}>
                  <ContactNumberInputElement
                    disabled={isManagerEditingCompletedSurvey && sendReportsToApi}
                  />
                  <AltContactNumberInputElement />
                </Col>
              </Row>

              <Typography style={{ fontWeight: 500 }}>Vehicle Details</Typography>
              <Row gutter={16}>
                <Col span={12}>
                  <InputElement label="Dealer" name="dealer_name" disabled={!isSuperUser} />
                  <InputElement label="Dealer Code" name="dealer_code" disabled={!isSuperUser} />
                  <InputElement
                    label="Dealer Location"
                    name="dealer_location"
                    disabled={!isSuperUser}
                  />
                  <BrandModelSelect
                    brandModels={brandModels}
                    disabled={isManagerEditingCompletedSurvey && sendReportsToApi}
                  />
                  <InputElement label="Model Variant" name="model_variant" />
                  <InputElement label="Model Colour" name="model_colour" />
                </Col>
                <Col span={12}>
                  <InputElement label="eCB Booking Code" name="ecb_booking_code" disabled />
                  <BrandSelect
                    onSelect={(val: string) => {
                      console.log(val);
                    }}
                    disabled
                  />
                  <InputElement label="Model Description" name="model_description" />
                  <InputElement label="Model Int Color" name="model_int_color" />
                  <InputElement label="Model Ext Color" name="model_ext_color" />
                </Col>
              </Row>

              <Typography style={{ fontWeight: 500 }}>Address and Schedule</Typography>
              <Row gutter={16}>
                <Col span={12}>
                  <InputElement
                    label="Booking Address"
                    name="booking_address"
                    rules={[{ required: true, message: "Please Enter Booking Address" }]}
                    disabled
                  />

                  <InputElement
                    label="Survey Address Line1"
                    name="address_line1"
                    disabled={isManagerEditingCompletedSurvey && sendReportsToApi}
                  />

                  <Form.Item
                    label="Scheduled Survey Date Time"
                    name="scheduled_survey_date_time"
                    rules={[{ required: true, message: "Please input Scheduled survey date Time" }]}
                  >
                    <Input type="datetime-local" />
                  </Form.Item>
                  <InputElement
                    label="Survey Locality"
                    name="locality"
                    disabled={isManagerEditingCompletedSurvey && sendReportsToApi}
                  />
                  <CustomerCitySelect
                    disabled={isManagerEditingCompletedSurvey && sendReportsToApi}
                  />
                  <CitySelect
                    onChange={onCityChange}
                    disabled={isManagerEditingCompletedSurvey && sendReportsToApi}
                  />
                  <StateSelect disabled={isManagerEditingCompletedSurvey && sendReportsToApi} />
                  <InputElement label="Site POC Name" name="site_poc_name" />
                </Col>
                <Col span={12}>
                  <PincodeInputElement
                    disabled={isManagerEditingCompletedSurvey && sendReportsToApi}
                  />
                  <InputElement
                    label="Survey Address Line2"
                    name="address_line2"
                    disabled={isManagerEditingCompletedSurvey && sendReportsToApi}
                  />
                  <ZoneSelect disabled={!isEditUser} required />
                  <InputElement label="Latitude" name="latitude" />
                  <InputElement label="Longitude" name="longitude" />
                  <Form.Item
                    label="Outstation for installer ?"
                    name="is_outstation"
                    rules={[{ required: true, message: "Please select outstation" }]}
                  >
                    <Select
                      placeholder="Select Outstation"
                      allowClear
                      showSearch
                      onChange={(value) => setOutStationForInstall(value)}
                      optionFilterProp="children"
                      filterOption={(inputValue, option: any) =>
                        option.label.toLowerCase().includes(inputValue.toLowerCase())
                      }
                      options={yesNoOptions}
                    ></Select>
                  </Form.Item>
                  {outStationForInstall && <Form.Item
                    label="Category"
                    name="category"
                    rules={[{ required: true, message: "Please select category" }]}
                  >
                    <Select
                      placeholder="Select Category"
                      allowClear
                      showSearch
                      disabled={isManager && (isApproved || isCompleted)}
                      optionFilterProp="children"
                      filterOption={(inputValue, option: any) =>
                        option.label.toLowerCase().includes(inputValue.toLowerCase())
                      }
                      options={categoryOptions}
                    ></Select>
                  </Form.Item>}

                  <Form.Item
                    label="Outstation for Client ?"
                    name="is_outstation_for_client"
                    rules={[{ required: true, message: "Please select outstation" }]}
                  >
                    <Select
                      placeholder="Select Outstation"
                      allowClear
                      showSearch
                      onChange={(value) => setOutstationClient(value)}
                      optionFilterProp="children"
                      filterOption={(inputValue, option: any) =>
                        option.label.toLowerCase().includes(inputValue.toLowerCase())
                      }
                      options={yesNoOptions}
                    ></Select>
                  </Form.Item>
                  {outstationClient &&
                    <NumberInputElement
                      disabled={isManager && (isApproved || isCompleted)}
                      label="Distance (in kms)"
                      name="outstation_distance"
                      rules={[
                        {
                          required: outstationDistRequired,
                          message: "Please Enter Outstation Distance",
                        },
                      ]} />}
                  <InputElement label="Site POC Contact" name="site_poc_contact" />
                </Col>
              </Row>

              <Typography style={{ fontWeight: 500 }}>Assignment</Typography>
              <Row gutter={16}>
                <Col span={12}>
                  <CityManagerSelect />
                  <DropDownSelectProps
                    label="Assigned Field Agent1"
                    name="assigned_field_agent1_id"
                    records={fieldAgents}
                    placeHolder="Select field agent"
                  />
                  <DropDownSelectProps
                    label="Assigned Field Agent2"
                    name="assigned_field_agent2_id"
                    records={fieldAgents}
                    placeHolder="Select field agent"
                  />
                </Col>
                <Col span={12}>
                  <DropDownSelectProps
                    label="Survey Partner"
                    name="survey_partner_id"
                    required
                    records={partnerData?.partners}
                    placeHolder="Select partner"
                    onSelect={handlePartnerChange}
                  />

                  <DropDownSelectProps
                    label="Assigned Partner User"
                    name="assigned_partner_user_id"
                    records={partnerManager}
                    placeHolder="Select partner user"
                  />
                  <InputElement label="Note For Agent" name="note_for_agent" />
                </Col>
              </Row>

              <Typography style={{ fontWeight: 500 }}>Other</Typography>
              <Row gutter={16}>
                <Col span={12}>
                  <CheckBoxInput
                    label="Customer Survey Confirmed"
                    name="customer_survey_confirmed"
                  />
                  <CheckBoxInput
                    label="Customer Ok With Recording"
                    name="customer_ok_with_recording"
                  />
                  <DropDownSelectProps
                    label="Broad Survey Findings 1"
                    name="summary_stage1"
                    records={broadSurveeyFinding1Options as []}
                    placeHolder="Select Survey Findings 1"
                    onSelect={handleSurveyFinding1Change}
                  />
                  <DropDownSelectProps
                    label="Broad Survey Findings 2"
                    name="summary_stage2"
                    records={broadSurveeyFinding2Options as []}
                    placeHolder="Select Survey Findings 2"
                    onSelect={handleSurveyFinding2Change}
                  />

                  <Form.Item
                    label="Is there a no show during survey ?"
                    name="is_no_show_during_survey"
                    rules={[{ required: true, message: "Please select" }]}
                  >
                    <Select
                      placeholder="Select Option"
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      filterOption={(inputValue, option: any) =>
                        option.label.toLowerCase().includes(inputValue.toLowerCase())
                      }
                      options={yesNoOptions}
                    ></Select>
                  </Form.Item>

                  <InputElement label="Charger Capacity" name="charger_capacity" />
                  <InputElement label="Charger Make Type" name="charger_make_type" />
                  {isSuperUser && <InputElement label="Cable Length" name="cable_length_new" />}
                  <InputElement
                    label="Charger Type"
                    name="obc"
                    disabled={!(isSuperUser || isMisUser)}
                  />
                  <Form.Item
                    label="Survey readiness information date"
                    name="survey_readiness_information_date"
                    rules={
                      !isEditMode
                        ? [
                          {
                            required: true,
                            message: "Please fill Survey readiness information date"
                          }
                        ]
                        : []
                    }
                  >
                    <Input type="date" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <InputElement label="Sanctioned Load" name="sanctioned_load" />
                  <InputElement label="Average Load Utilised" name="avg_load_utilized" />
                  <InputElement label="VDC" name="vdc" />
                  <InputElement label="MDI" name="mdi" />
                  <InputElement label="Cable Gauge" name="cable_gauge" />
                  <InputElement label="Remarks" name="remarks" />
                  <CheckBoxInput label="Extra Cable Customer Pay" name="extra_cable_customer_pay" />

                  <Form.Item
                    label="Is this client (OEM) paid - Survey ?"
                    name="customer_billing"
                    rules={[{ required: true, message: "Please select option" }]}
                  >
                    <Select
                      placeholder="Please select option"
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      filterOption={(inputValue, option: any) =>
                        option.label.toLowerCase().includes(inputValue.toLowerCase())
                      }
                      options={customerBillingOptions}
                    ></Select>
                  </Form.Item>

                  <Form.Item
                    label="Site Feasibility Status"
                    name="site_feasibility_status"
                    rules={[{ required: true, message: "Please select option" }]}
                  >
                    <Select
                      placeholder="Please select option"
                      allowClear
                      showSearch
                      disabled={isApproved && !(isMisUser || isSuperUser)}
                      optionFilterProp="children"
                      filterOption={(inputValue, option: any) =>
                        option.label.toLowerCase().includes(inputValue.toLowerCase())
                      }
                      options={yesNoOptions}
                    ></Select>
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}
          {(isFinanceController(user) || isSuperUser) && (
            <>
              <Row gutter={16}>
                <Col span={12}>
                  <InputElement
                    label="Complementary Cable Length"
                    name="complementary_cable_length"
                  />
                </Col>
                <Col span={12}>
                  <InputElement label="Pricing Per Meter" name="pricing_per_meter" />
                </Col>
                <Col span={12}>
                  <InputElement label="Payment Remarks" name="payment_remarks" />
                </Col>
                <Col span={12}>
                  <InputElement label="Invoice Remarks" name="invoice_remarks" />
                </Col>
              </Row>
            </>
          )}
          {(isManagerEditingCompletedSurvey || isSuperUser) && (
            <>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Survey readiness information date"
                    name="survey_readiness_information_date"
                    rules={
                      !isEditMode
                        ? [
                          {
                            required: true,
                            message: "Please fill Survey readiness information date"
                          }
                        ]
                        : []
                    }
                  >
                    <Input disabled={!isEditUser} type="date" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="SOW completed (Yes/No)?" name="is_sow_completed">
                    <Select
                      placeholder="Select Option"
                      allowClear
                      showSearch
                      disabled
                      optionFilterProp="children"
                      filterOption={(inputValue, option: any) =>
                        option.label.toLowerCase().includes(inputValue.toLowerCase())
                      }
                      options={yesNoOptions}
                    ></Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Site Feasibility Status"
                    name="site_feasibility_status"
                    rules={[{ required: true, message: "Please select option" }]}
                  >
                    <Select
                      placeholder="Please select option"
                      allowClear
                      showSearch
                      disabled={isManagerEditingCompletedSurvey}
                      optionFilterProp="children"
                      filterOption={(inputValue, option: any) =>
                        option.label.toLowerCase().includes(inputValue.toLowerCase())
                      }
                      options={yesNoOptions}
                    ></Select>
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          {isMisUserEditingCompletedSurvey && (
            <Row gutter={16}>
              <Col span={12}>
                <InputElement label="Survey Address Line1" name="address_line1" />
                <CustomerCitySelect />
                <CityManagerSelect />
                <ZoneSelect required />
                <Form.Item
                  label="Outstation for installer ?"
                  name="is_outstation"
                  rules={[{ required: true, message: "Please select outstation" }]}
                >
                  <Select
                    placeholder="Select Outstation"
                    allowClear
                    showSearch
                    onChange={(value) => setOutStationForInstall(value)}
                    optionFilterProp="children"
                    filterOption={(inputValue, option: any) =>
                      option.label.toLowerCase().includes(inputValue.toLowerCase())
                    }
                    options={yesNoOptions}
                  ></Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <PincodeInputElement
                  disabled={isManagerEditingCompletedSurvey && sendReportsToApi}
                />
                <InputElement label="Survey Address Line2" name="address_line2" />
                <StateSelect />
                <Form.Item
                  label="Outstation for Client ?"
                  name="is_outstation_for_client"
                  rules={[{ required: true, message: "Please select outstation" }]}
                >
                  <Select
                    placeholder="Select Outstation"
                    allowClear
                    showSearch
                    onChange={(value) => setOutstationClient(value)}
                    optionFilterProp="children"
                    filterOption={(inputValue, option: any) =>
                      option.label.toLowerCase().includes(inputValue.toLowerCase())
                    }
                    options={yesNoOptions}
                  ></Select>
                </Form.Item>
                {outstationClient &&
                  <NumberInputElement
                    disabled={isManager && (isApproved || isCompleted)}
                    label="Distance (in kms)"
                    name="outstation_distance"
                    rules={[
                      {
                        required: outstationDistRequired,
                        message: "Please Enter Outstation Distance",
                      },
                    ]} />}
                {outStationForInstall && <Form.Item
                  label="Category"
                  name="category"
                  rules={[{ required: true, message: "Please select category" }]}
                >
                  <Select
                    placeholder="Select Category"
                    allowClear
                    showSearch
                    disabled={isManager && (isApproved || isCompleted)}
                    optionFilterProp="children"
                    filterOption={(inputValue, option: any) =>
                      option.label.toLowerCase().includes(inputValue.toLowerCase())
                    }
                    options={categoryOptions}
                  ></Select>
                </Form.Item>}
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Site Feasibility Status"
                  name="site_feasibility_status"
                  rules={[{ required: true, message: "Please select option" }]}
                >
                  <Select
                    placeholder="Please select option"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    filterOption={(inputValue, option: any) =>
                      option.label.toLowerCase().includes(inputValue.toLowerCase())
                    }
                    options={yesNoOptions}
                  ></Select>
                </Form.Item>
              </Col>
            </Row>
          )}

          {(isManager || isMisUser || isSuperUser) && !isMisUserEditingCompletedSurvey && (
            <>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Date of payment for extra cable if applicable"
                    name="extra_cable_payment_date"
                    rules={
                      extraCableStatus == "completed"
                        ? [
                          {
                            required: true,
                            message: "Please Enter Date of payment for extra cable if applicable"
                          }
                        ]
                        : []
                    }
                  >
                    <Input type="date" />
                  </Form.Item>
                  <Form.Item
                    label="SOW completion date"
                    name="sow_completion_date"
                    rules={
                      sowStatus == "completed"
                        ? [
                          {
                            required: true,
                            message: "Please Enter SOW completion date"
                          }
                        ]
                        : []
                    }
                  >
                    <Input type="date" />
                  </Form.Item>
                  <InputElement label="UML SOW Remarks" name="uml_sow_survey_remarks" />
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Status of payment for extra cable"
                    name="extra_cable_payment_status"
                  >
                    <Select
                      placeholder="Please select option"
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option: any) =>
                        option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                      options={completionStatusOptions}
                    ></Select>
                  </Form.Item>
                  <Form.Item label="Status of customer side SOW" name="sow_status">
                    <Select
                      placeholder="Please select option"
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option: any) =>
                        option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                      options={completionStatusOptions}
                    ></Select>
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}
          <Form.Item wrapperCol={{ offset: 19, span: 24 }}>
            <Row>
              <Col>
                <Space size={"middle"}>
                  <Button
                    type="default"
                    onClick={() => {
                      form.resetFields();
                      onGoBack();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    disabled={
                      isFinanceController(user) || isManagerEditingCompletedSurvey
                        ? false
                        : loading ||
                        (isEditMode &&
                          !isAdminUser &&
                          !isMisUserEditingCompletedSurvey &&
                          selectedSurveyData?.status === "completed")
                    }
                  >
                    {isEditMode ? "Update" : "Add"}
                  </Button>
                </Space>
              </Col>
            </Row>
          </Form.Item>
        </Form>
      </div>
    </>
  );
};

export default SurveyForm;
